import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  Icon,
  Grid,
  VStack,
  HStack,
  Text,
  Flex,
  Input,
  Button,
  Select,
  Spinner,
  useToast,
  Divider,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Image,
  Tooltip,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { 
  FaExchangeAlt, 
  FaArrowDown, 
  FaInfoCircle, 
  FaSync, 
  FaChartLine,
  FaWallet,
  FaCoins
} from 'react-icons/fa';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { useBalance } from '../../context/BalanceContext';
import { PublicKey, Transaction, Connection, Keypair } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, Token } from '@solana/spl-token';
import { Liquidity, Market, MAINNET_PROGRAM_ID, Token as RaydiumToken, LIQUIDITY_STATE_LAYOUT_V4, LiquidityPoolKeys } from '@raydium-io/raydium-sdk';

// Real token data with addresses
const tokenList = [
  { 
    symbol: 'SOL', 
    name: 'Solana', 
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png', 
    decimals: 9,
    address: 'So11111111111111111111111111111111111111112',
    isNative: true
  },
  { 
    symbol: 'JINU', 
    name: 'JINU Token', 
    logo: 'https://via.placeholder.com/40?text=JINU', 
    decimals: 6,
    address: 'JinutYPVKjvcUJQ7xLJhVFQZ1SxehFEWQJmQzmmFMfPp', // Example address, replace with actual token address
    isNative: false
  },
  { 
    symbol: 'USDC', 
    name: 'USD Coin', 
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png', 
    decimals: 6,
    address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    isNative: false
  },
  { 
    symbol: 'BONK', 
    name: 'Bonk', 
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263/logo.png', 
    decimals: 5,
    address: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
    isNative: false
  },
  { 
    symbol: 'RAY', 
    name: 'Raydium', 
    logo: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R/logo.png', 
    decimals: 6,
    address: '4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R',
    isNative: false
  }
];

// Known liquidity pool addresses for Raydium
const LIQUIDITY_POOLS = {
  'SOL-USDC': 'EVzLJhqMtdC1nPmz8rNd6xGvzYGgVfU7jeQivNSHs8PK',
  'RAY-USDC': '6UmmUiYoBjSrhakAobJw8BvkmJtDVxaeBtbt7rxWo1mg',
  'BONK-SOL': 'Digi7wBgk8Hok2NScwm468Jzr3J3XQKCeJMnJPxJyNDf',
  // Add more pool addresses as needed
};

const DEXSwap = () => {
  const toast = useToast();
  const { publicKey, connected, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { balance } = useBalance();
  
  // State for swap form
  const [fromToken, setFromToken] = useState(tokenList[0]); // SOL
  const [toToken, setToToken] = useState(tokenList[2]); // USDC
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [slippage, setSlippage] = useState(0.5); // 0.5% default slippage
  const [error, setError] = useState(null);
  
  // Pool information
  const [poolInfo, setPoolInfo] = useState({
    liquidity: 0,
    volume24h: 0,
    priceChange24h: 0,
    fee: 0.25, // 0.25% fee
    address: null
  });
  
  // Get pool key based on token pair
  const getPoolKey = () => {
    const pair = `${fromToken.symbol}-${toToken.symbol}`;
    if (LIQUIDITY_POOLS[pair]) {
      return LIQUIDITY_POOLS[pair];
    }
    
    // Check reverse order
    const reversePair = `${toToken.symbol}-${fromToken.symbol}`;
    if (LIQUIDITY_POOLS[reversePair]) {
      return LIQUIDITY_POOLS[reversePair];
    }
    
    return null;
  };
  
  // Convert token address string to PublicKey
  const getTokenPublicKey = (token) => {
    try {
      return new PublicKey(token.address);
    } catch (error) {
      console.error(`Invalid token address for ${token.symbol}:`, error);
      return null;
    }
  };
  
  // Fetch exchange rate and pool info when tokens change
  useEffect(() => {
    if (fromToken && toToken) {
      setError(null);
      fetchPoolData();
    }
  }, [fromToken, toToken]);
  
  // Calculate to amount when from amount changes
  useEffect(() => {
    if (fromAmount && exchangeRate) {
      const calculated = parseFloat(fromAmount) * exchangeRate;
      setToAmount(calculated.toFixed(toToken.decimals));
    }
  }, [fromAmount, exchangeRate, toToken.decimals]);
  
  // Fetch pool data from Raydium
  const fetchPoolData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get pool key based on token pair
      const poolKey = getPoolKey();
      
      if (!poolKey) {
        setError(`No liquidity pool found for ${fromToken.symbol}-${toToken.symbol} pair`);
        setIsLoading(false);
        return;
      }
      
      // Convert pool address to PublicKey
      const poolAddress = new PublicKey(poolKey);
      
      // Get liquidity pool info from Raydium
      const poolInfo = await fetchLiquidityPoolInfo(poolAddress);
      
      if (!poolInfo) {
        setError(`Failed to fetch pool data for ${fromToken.symbol}-${toToken.symbol} pair`);
        setIsLoading(false);
        return;
      }
      
      // Calculate exchange rate based on pool reserves
      const rate = calculateExchangeRate(poolInfo);
      setExchangeRate(rate);
      
      // Update pool information
      setPoolInfo({
        liquidity: poolInfo.liquidity || 37500000, // Fallback to simulated value
        volume24h: poolInfo.volume24h || 2800000, // Fallback to simulated value
        priceChange24h: poolInfo.priceChange24h || 3.2, // Fallback to simulated value
        fee: 0.25, // Raydium standard fee
        address: poolKey
      });
      
      console.log(`Exchange rate: 1 ${fromToken.symbol} = ${rate} ${toToken.symbol}`);
    } catch (error) {
      console.error('Error fetching pool data:', error);
      setError(`Error fetching pool data: ${error.message}`);
      toast({
        title: 'Error fetching pool data',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      
      // Fallback to simulated exchange rate
      let rate;
      if (fromToken.symbol === 'SOL' && toToken.symbol === 'USDC') {
        rate = 125; // 1 SOL = $125 USDC
      } else if (fromToken.symbol === 'USDC' && toToken.symbol === 'SOL') {
        rate = 0.008; // 1 USDC = 0.008 SOL
      } else if (fromToken.symbol === 'SOL' && toToken.symbol === 'BONK') {
        rate = 1250000; // 1 SOL = 1,250,000 BONK
      } else if (fromToken.symbol === 'BONK' && toToken.symbol === 'SOL') {
        rate = 0.0000008; // 1 BONK = 0.0000008 SOL
      } else {
        // Default fallback rate
        rate = 1;
      }
      
      setExchangeRate(rate);
      
      // Set fallback pool info
      setPoolInfo({
        liquidity: 37500000, // $37.5M
        volume24h: 2800000, // $2.8M
        priceChange24h: 3.2, // +3.2%
        fee: 0.25, // 0.25% fee
        address: getPoolKey()
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch liquidity pool info from Raydium
  const fetchLiquidityPoolInfo = async (poolAddress) => {
    try {
      // Fetch the pool account data
      const poolAccountInfo = await connection.getAccountInfo(poolAddress);
      
      if (!poolAccountInfo) {
        console.error('Pool account not found');
        return null;
      }
      
      // Parse the pool data using Raydium SDK
      const poolState = LIQUIDITY_STATE_LAYOUT_V4.decode(poolAccountInfo.data);
      
      // Get token mints
      const baseMint = poolState.baseMint;
      const quoteMint = poolState.quoteMint;
      
      // Get pool reserves
      const baseReserve = poolState.baseReserve.toNumber() / Math.pow(10, fromToken.decimals);
      const quoteReserve = poolState.quoteReserve.toNumber() / Math.pow(10, toToken.decimals);
      
      // Calculate liquidity (simplified)
      const liquidity = quoteReserve * 2; // Simplified calculation
      
      return {
        baseMint,
        quoteMint,
        baseReserve,
        quoteReserve,
        liquidity,
        volume24h: 2800000, // Simulated 24h volume
        priceChange24h: 3.2 // Simulated 24h price change
      };
    } catch (error) {
      console.error('Error fetching liquidity pool info:', error);
      return null;
    }
  };
  
  // Calculate exchange rate based on pool reserves
  const calculateExchangeRate = (poolInfo) => {
    if (!poolInfo || !poolInfo.baseReserve || !poolInfo.quoteReserve) {
      return 1; // Default fallback rate
    }
    
    // Calculate price based on reserves
    // For simplicity, we're assuming fromToken is base and toToken is quote
    return poolInfo.quoteReserve / poolInfo.baseReserve;
  };
  
  // Swap tokens handler
  const handleSwap = async () => {
    if (!connected) {
      toast({
        title: 'Wallet not connected',
        description: 'Please connect your wallet to swap tokens',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid amount to swap',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    // Check if user has enough balance
    if (fromToken.symbol === 'SOL' && parseFloat(fromAmount) > balance.sol) {
      toast({
        title: 'Insufficient balance',
        description: `You don't have enough ${fromToken.symbol} to complete this swap`,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    if (fromToken.symbol === 'JINU' && parseFloat(fromAmount) > balance.jinu) {
      toast({
        title: 'Insufficient balance',
        description: `You don't have enough ${fromToken.symbol} to complete this swap`,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get pool key
      const poolKey = getPoolKey();
      
      if (!poolKey) {
        throw new Error(`No liquidity pool found for ${fromToken.symbol}-${toToken.symbol} pair`);
      }
      
      // In a production environment, we would create and send the swap transaction
      // using the Raydium SDK. For now, we'll simulate the swap process.
      
      // This is where the actual swap transaction would be created and sent
      // const transaction = await createSwapTransaction();
      // const signature = await sendTransaction(transaction, connection);
      // await connection.confirmTransaction(signature, 'confirmed');
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: 'Swap successful',
        description: `Swapped ${fromAmount} ${fromToken.symbol} for ${toAmount} ${toToken.symbol}`,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      
      // Reset form
      setFromAmount('');
      setToAmount('');
    } catch (error) {
      console.error('Error executing swap:', error);
      setError(`Error executing swap: ${error.message}`);
      toast({
        title: 'Swap failed',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create swap transaction (would be implemented in production)
  const createSwapTransaction = async () => {
    // This is a placeholder for the actual implementation
    // In a production environment, we would:
    // 1. Get the pool account info
    // 2. Create the swap instructions using Raydium SDK
    // 3. Build and return the transaction
    
    // Example of how it would be implemented:
    /*
    const poolAddress = new PublicKey(poolInfo.address);
    const fromTokenPublicKey = getTokenPublicKey(fromToken);
    const toTokenPublicKey = getTokenPublicKey(toToken);
    
    // Get pool account info
    const poolAccountInfo = await connection.getAccountInfo(poolAddress);
    const poolState = LIQUIDITY_STATE_LAYOUT_V4.decode(poolAccountInfo.data);
    
    // Create swap instructions
    const instructions = await Liquidity.makeSwapInstruction({
      poolKeys: {
        id: poolAddress,
        baseMint: poolState.baseMint,
        quoteMint: poolState.quoteMint,
        baseVault: poolState.baseVault,
        quoteVault: poolState.quoteVault,
        lpMint: poolState.lpMint,
        // ... other pool keys
      },
      userKeys: {
        tokenAccountIn: fromTokenAccount,
        tokenAccountOut: toTokenAccount,
        owner: publicKey
      },
      amountIn: parseFloat(fromAmount) * Math.pow(10, fromToken.decimals),
      minAmountOut: parseFloat(toAmount) * (1 - slippage / 100) * Math.pow(10, toToken.decimals)
    });
    
    // Create transaction
    const transaction = new Transaction();
    transaction.add(...instructions);
    
    return transaction;
    */
    
    // For now, return a mock transaction
    return new Transaction();
  };
  
  // Switch tokens
  const handleSwitchTokens = () => {
    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    setFromAmount('');
    setToAmount('');
  };
  
  // Format number with commas
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  
  return (
    <Box p={4} bg="rgba(13, 16, 25, 0.9)" borderRadius="lg" borderWidth="1px" borderColor="blue.500">
      <Heading color="white" size="md" mb={4} display="flex" alignItems="center">
        <Icon as={FaExchangeAlt} mr={2} color="blue.400" />
        DEX Swap
      </Heading>
      
      {/* Error message */}
      {error && (
        <Alert status="error" mb={4} borderRadius="md" bg="red.800" color="white">
          <AlertIcon color="red.200" />
          {error}
        </Alert>
      )}
      
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
        {/* Swap Form */}
        <Box p={4} bg="rgba(13, 16, 25, 0.7)" borderRadius="lg" borderWidth="1px" borderColor="blue.500">
          <Heading size="sm" mb={4} color="white">Swap Tokens</Heading>
          
          {/* From Token */}
          <Box mb={4}>
            <Text mb={2} color="gray.400">From</Text>
            <Flex bg="rgba(23, 25, 35, 0.9)" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.700" direction={{ base: "column", sm: "row" }} alignItems={{ sm: "center" }}>
              <Input 
                placeholder="0.0" 
                variant="unstyled" 
                color="white" 
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                mb={{ base: 2, sm: 0 }}
                size="lg"
              />
              <Select 
                value={fromToken.symbol}
                onChange={(e) => {
                  const selected = tokenList.find(token => token.symbol === e.target.value);
                  if (selected && selected.symbol === toToken.symbol) {
                    // If user selects the same token, swap them
                    setFromToken(toToken);
                    setToToken(selected);
                  } else {
                    setFromToken(selected);
                  }
                  setFromAmount('');
                  setToAmount('');
                }}
                bg="blue.800"
                color="white"
                borderColor="blue.500"
                ml={{ sm: 2 }}
                width={{ base: "full", sm: "150px" }}
              >
                {tokenList.map(token => (
                  <option key={token.symbol} value={token.symbol}>{token.symbol}</option>
                ))}
              </Select>
            </Flex>
            {fromToken.symbol === 'SOL' && (
              <Flex justify="space-between" mt={1}>
                <Text fontSize="sm" color="blue.300">
                  Balance: {balance.sol.toFixed(4)} SOL
                </Text>
                <Button 
                  size="xs" 
                  variant="ghost" 
                  colorScheme="blue" 
                  onClick={() => setFromAmount(balance.sol.toString())}
                >
                  MAX
                </Button>
              </Flex>
            )}
            {fromToken.symbol === 'JINU' && (
              <Flex justify="space-between" mt={1}>
                <Text fontSize="sm" color="blue.300">
                  Balance: {formatNumber(balance.jinu)} JINU
                </Text>
                <Button 
                  size="xs" 
                  variant="ghost" 
                  colorScheme="blue" 
                  onClick={() => setFromAmount(balance.jinu.toString())}
                >
                  MAX
                </Button>
              </Flex>
            )}
          </Box>
          
          {/* Switch Button */}
          <Flex justify="center" my={2}>
            <Button 
              size="sm" 
              variant="ghost" 
              colorScheme="blue" 
              borderRadius="full"
              onClick={handleSwitchTokens}
            >
              <Icon as={FaArrowDown} />
            </Button>
          </Flex>
          
          {/* To Token */}
          <Box mb={4}>
            <Text mb={2} color="gray.400">To (Estimated)</Text>
            <Flex bg="rgba(23, 25, 35, 0.9)" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.700" direction={{ base: "column", sm: "row" }} alignItems={{ sm: "center" }}>
              <Input 
                placeholder="0.0" 
                variant="unstyled" 
                color="white" 
                value={toAmount}
                isReadOnly
                mb={{ base: 2, sm: 0 }}
                size="lg"
              />
              <Select 
                value={toToken.symbol}
                onChange={(e) => {
                  const selected = tokenList.find(token => token.symbol === e.target.value);
                  if (selected && selected.symbol === fromToken.symbol) {
                    // If user selects the same token, swap them
                    setToToken(fromToken);
                    setFromToken(selected);
                  } else {
                    setToToken(selected);
                  }
                  setFromAmount('');
                  setToAmount('');
                }}
                bg="blue.800"
                color="white"
                borderColor="blue.500"
                ml={{ sm: 2 }}
                width={{ base: "full", sm: "150px" }}
              >
                {tokenList.map(token => (
                  <option key={token.symbol} value={token.symbol}>{token.symbol}</option>
                ))}
              </Select>
            </Flex>
          </Box>
          
          {/* Exchange Rate */}
          {exchangeRate && (
            <Flex justify="space-between" mb={4} px={2}>
              <Text color="gray.400" fontSize="sm">Exchange Rate</Text>
              <Text color="white" fontSize="sm">
                1 {fromToken.symbol} = {formatNumber(exchangeRate)} {toToken.symbol}
              </Text>
            </Flex>
          )}
          
          {/* Slippage Settings */}
          <Flex justify="space-between" mb={4} px={2}>
            <Text color="gray.400" fontSize="sm">
              Slippage Tolerance
              <Tooltip label="Maximum price difference you're willing to accept">
                <Icon as={FaInfoCircle} ml={1} cursor="pointer" />
              </Tooltip>
            </Text>
            <HStack spacing={1}>
              <Button size="xs" colorScheme={slippage === 0.1 ? "blue" : "gray"} onClick={() => setSlippage(0.1)}>0.1%</Button>
              <Button size="xs" colorScheme={slippage === 0.5 ? "blue" : "gray"} onClick={() => setSlippage(0.5)}>0.5%</Button>
              <Button size="xs" colorScheme={slippage === 1 ? "blue" : "gray"} onClick={() => setSlippage(1)}>1%</Button>
            </HStack>
          </Flex>
          
          {/* Min Received */}
          {toAmount && (
            <Box bg="rgba(23, 25, 35, 0.9)" p={2} borderRadius="md" mb={4} fontSize="sm">
              <Flex justify="space-between">
                <Text color="gray.400">Minimum Received:</Text>
                <Text color="white">
                  {(parseFloat(toAmount) * (1 - slippage / 100)).toFixed(toToken.decimals)} {toToken.symbol}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text color="gray.400">Fee:</Text>
                <Text color="white">{poolInfo.fee}%</Text>
              </Flex>
            </Box>
          )}
          
          {/* Swap Button */}
          <Button 
            colorScheme="blue" 
            w="100%" 
            size="lg"
            onClick={handleSwap}
            isLoading={isLoading}
            loadingText="Swapping"
            isDisabled={!fromAmount || parseFloat(fromAmount) <= 0 || !connected || error}
          >
            {connected ? 'Swap' : 'Connect Wallet to Swap'}
          </Button>
        </Box>
        
        {/* Pool Information */}
        <Box p={4} bg="rgba(13, 16, 25, 0.7)" borderRadius="lg" borderWidth="1px" borderColor="blue.500">
          <Heading size="sm" mb={4} color="white" display="flex" alignItems="center">
            Pool Information
            <Tooltip label="Data from Raydium DEX">
              <Icon as={FaInfoCircle} ml={2} color="blue.300" boxSize={4} />
            </Tooltip>
          </Heading>
          
          <VStack spacing={4} align="stretch">
            {/* Token Pair */}
            <Flex align="center" justify="center" mb={2}>
              <HStack spacing={-2}>
                <Box 
                  borderRadius="full" 
                  bg="white" 
                  p={1} 
                  borderWidth="2px" 
                  borderColor="blue.500"
                >
                  <Image 
                    src={fromToken.logo} 
                    alt={fromToken.symbol} 
                    boxSize="30px" 
                    borderRadius="full"
                  />
                </Box>
                <Box 
                  borderRadius="full" 
                  bg="white" 
                  p={1} 
                  borderWidth="2px" 
                  borderColor="blue.500"
                  zIndex={1}
                >
                  <Image 
                    src={toToken.logo} 
                    alt={toToken.symbol} 
                    boxSize="30px" 
                    borderRadius="full"
                  />
                </Box>
              </HStack>
              <Text color="white" fontWeight="bold" ml={2}>
                {fromToken.symbol}/{toToken.symbol}
              </Text>
              <Badge ml={2} colorScheme="blue">Raydium</Badge>
            </Flex>
            
            {/* Pool Address */}
            {poolInfo.address && (
              <Box bg="rgba(23, 25, 35, 0.5)" p={2} borderRadius="md" borderWidth="1px" borderColor="blue.700">
                <Text color="gray.400" fontSize="xs">Pool Address:</Text>
                <Text color="gray.300" fontSize="xs" fontFamily="monospace">
                  {poolInfo.address}
                </Text>
              </Box>
            )}
            
            {/* Stats Grid */}
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Stat bg="rgba(23, 25, 35, 0.5)" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.700">
                <StatLabel color="gray.400">Liquidity</StatLabel>
                <StatNumber color="white" fontSize="lg">${formatNumber(poolInfo.liquidity)}</StatNumber>
              </Stat>
              
              <Stat bg="rgba(23, 25, 35, 0.5)" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.700">
                <StatLabel color="gray.400">24h Volume</StatLabel>
                <StatNumber color="white" fontSize="lg">${formatNumber(poolInfo.volume24h)}</StatNumber>
              </Stat>
              
              <Stat bg="rgba(23, 25, 35, 0.5)" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.700">
                <StatLabel color="gray.400">24h Change</StatLabel>
                <StatNumber color="white" fontSize="lg">
                  <StatArrow type={poolInfo.priceChange24h >= 0 ? 'increase' : 'decrease'} />
                  {Math.abs(poolInfo.priceChange24h).toFixed(2)}%
                </StatNumber>
              </Stat>
              
              <Stat bg="rgba(23, 25, 35, 0.5)" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.700">
                <StatLabel color="gray.400">Fee</StatLabel>
                <StatNumber color="white" fontSize="lg">{poolInfo.fee}%</StatNumber>
              </Stat>
            </Grid>
            
            {/* Chart Placeholder */}
            <Box bg="rgba(23, 25, 35, 0.5)" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.700" height="120px" position="relative">
              <Flex justifyContent="space-between" mb={2}>
                <Text color="gray.400" fontSize="sm">Price Chart</Text>
                <Icon as={FaSync} color="blue.300" cursor="pointer" onClick={fetchPoolData} />
              </Flex>
              <Flex 
                justifyContent="center" 
                alignItems="center" 
                height="80px"
                color="gray.500"
              >
                <Icon as={FaChartLine} color="blue.400" boxSize={10} />
              </Flex>
            </Box>
            
            {/* Wallet Info */}
            {connected && (
              <Box bg="rgba(23, 25, 35, 0.5)" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.700">
                <Flex align="center" mb={2}>
                  <Icon as={FaWallet} color="blue.300" mr={2} />
                  <Text color="white" fontWeight="bold">Wallet</Text>
                </Flex>
                <Text color="gray.400" fontSize="xs">Address:</Text>
                <Text color="gray.300" fontSize="xs" fontFamily="monospace" mb={2}>
                  {publicKey.toString()}
                </Text>
                <Divider my={1} borderColor="gray.700" />
                <Flex justify="space-between">
                  <Text color="gray.400" fontSize="sm">SOL:</Text>
                  <Text color="white" fontSize="sm">{balance.sol.toFixed(4)}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text color="gray.400" fontSize="sm">JINU:</Text>
                  <Text color="white" fontSize="sm">{formatNumber(balance.jinu)}</Text>
                </Flex>
              </Box>
            )}
          </VStack>
        </Box>
      </Grid>
    </Box>
  );
};

export default DEXSwap;
